/**
 * @fileoverview Terms of Service Page
 *
 * Purpose: Public terms of service for Morgan / Raconteur Operations Pty Ltd.
 *          Content is hosted directly in the app (self-contained) for a
 *          professional, branded presentation.
 * URL: /terms
 * Protection: Public (no auth required)
 */

import { createFileRoute } from "@tanstack/react-router";
import { LegalLayout } from "./_home/-ui.legal-layout";

export const Route = createFileRoute("/terms")({
  component: TermsPage,
});

function TermsPage() {
  return (
    <LegalLayout
      title="Terms and Conditions"
      lastUpdated="Last updated: 17 September 2026"
    >
      <h2>1. Introduction</h2>
      <p>
        1.1 These Terms and Conditions (<strong>Terms</strong>) are a legal
        agreement between you or the entity you represent (
        <strong>Subscriber</strong>, <strong>you</strong>, <strong>your</strong>
        ) and Raconteur Operations Pty Ltd (trading as Morgan) ABN 37 675 499
        108 (<strong>we</strong>, <strong>us</strong>, <strong>our</strong>).
        These Terms govern your access to and use of the Morgan platform and
        related services (<strong>Services</strong>).
      </p>
      <p>
        1.2 By accessing or using the Services, you agree to be bound by these
        Terms. If you are accepting these Terms on behalf of a company or other
        legal entity, you represent that you have authority to bind that entity,
        and references to you or Subscriber in these Terms refer to that entity.
      </p>
      <p>
        1.3 These Terms apply together with any order form or separate pilot
        agreement entered into between us and the Subscriber which refers to
        these Terms. In the event of any inconsistency between these Terms and
        the order form or pilot agreement, these Terms prevail to the extent of
        the inconsistency, unless the order form or pilot agreement expressly
        states that it overrides the specific clause in these Terms by number.
      </p>
      <p>
        1.4 We may update these Terms from time to time. We will give you
        reasonable written notice of any material change. If the change
        increases your obligations or decreases your rights under these Terms,
        the change will not take effect until you expressly accept the updated
        terms. For all other changes, your continued use of the Services after
        the effective date of any change constitutes acceptance of the updated
        Terms.
      </p>

      <h2>2. The Services</h2>

      <h3>2.1 Access and Availability</h3>
      <p>
        2.1.1 We grant the Subscriber a limited, non-exclusive, non-transferable
        right to access and use the Services and authorise each Authorised User
        to access and use the Services during the Term for the Subscriber's
        internal business purposes in accordance with these Terms.
      </p>
      <p>
        2.1.2 We will use commercially reasonable efforts to make the Services
        available 24 hours a day, 7 days a week, except for:
      </p>
      <p>
        (a) scheduled maintenance, for which we will provide advance notice
        where practicable; and
      </p>
      <p>
        (b) unplanned outages, emergency maintenance, or events outside our
        reasonable control.
      </p>
      <p>
        2.1.3 The Subscriber is responsible for obtaining and maintaining, at
        its own cost, the hardware, software and internet connectivity necessary
        to access the Services.
      </p>

      <h3>2.2 Account Administration</h3>
      <p>
        2.2.1 The Subscriber must designate one or more administrators to manage
        its account and its Authorised Users. Administrators may have access to
        usage data and login information for Authorised Users within the
        Subscriber's account.
      </p>
      <p>
        2.2.2 The Subscriber is responsible for all activity that occurs under
        its account, including the acts and omissions of its Authorised Users.
      </p>
      <p>2.2.3 The Subscriber must:</p>
      <p>
        (a) keep, and must ensure that each Authorised User keeps, all access
        credentials confidential; and
      </p>
      <p>
        (b) notify us immediately at{" "}
        <a href="mailto:legal@morgan.so">legal@morgan.so</a> if the Subscriber
        or any of its Authorised Users suspects any unauthorised access to or
        use of its account.
      </p>
      <p>
        2.2.4 Each user licence is personal to the Authorised User to whom it is
        assigned and may not be shared with, transferred to, or used by any
        other individual unless otherwise agreed in writing.
      </p>

      <h3>2.3 Acceptable Use and Usage Restrictions</h3>
      <p>
        2.3.1 The Subscriber must not, and must ensure that its Authorised Users
        do not:
      </p>
      <p>
        (a) use the Services in any way that infringes, misappropriates or
        violates the rights of any person;
      </p>
      <p>
        (b) sub-license, sell, resell, rent, lease or otherwise transfer,
        distribute or make the Services available to any third party;
      </p>
      <p>
        (c) directly or indirectly modify, reverse engineer, decompile,
        disassemble or otherwise derive the source code, algorithms, model
        weights, prompts, architecture or underlying technology of the Services;
      </p>
      <p>
        (d) modify, adapt, translate, port or create derivative works of the
        Services;
      </p>
      <p>
        (e) use any automated or programmatic method to extract data or Outputs
        from the Services, other than a method we expressly make available;
      </p>
      <p>
        (f) use the Services, or any Output, to build, train, benchmark or
        improve any product or service that competes with the Services;
      </p>
      <p>
        (g) access or use the Services from, or for the benefit of any person
        located in, any Sanctioned Jurisdiction, or provide access to any person
        designated on an applicable sanctions or restricted party list;
      </p>
      <p>
        (h) submit to the Services any payment card data (including card
        numbers, cardholder names, expiry dates or security codes), or sensitive
        information (as defined in the Privacy Act), including any genetic or
        biometric data or health information, unless we have expressly agreed in
        writing to receive it;
      </p>
      <p>
        (i) use the Services in a manner that interferes, or attempts to
        interfere, with the proper working, integrity or security of the
        Services, or that imposes an unreasonable or disproportionate load on
        our infrastructure; or
      </p>
      <p>
        (j) use the Services in breach of any applicable law, or of any
        professional obligation or code of conduct binding on the Subscriber or
        any of its Authorised Users.
      </p>
      <p>
        2.3.2 The Subscriber is responsible for all acts and omissions of its
        Authorised Users, and their compliance with these Terms, and any act or
        omission by an Authorised User that would constitute a breach of these
        Terms will be deemed a breach of these Terms by the Subscriber.
      </p>
      <p>
        2.3.3 If we have reasonable grounds to suspect a breach of these Terms,
        we may:
      </p>
      <p>
        (a) notify you by email (each, a <strong>Service Notice</strong>) and
        request that you take immediate and appropriate action within 10
        business days (or such other reasonable period specified in the Service
        Notice); and
      </p>
      <p>
        (b) immediately suspend your access, or the access of an individual
        administrator or Authorised User, on written notice if:
      </p>
      <p>
        (i) we reasonably believe that the breach poses an immediate risk of
        harm to us, to the Services or to any third party;
      </p>
      <p>
        (ii) you do not comply with a Service Notice within the time specified
        in it; or
      </p>
      <p>(iii) the breach is not capable of remedy.</p>
      <p>
        2.3.4 If you fail to take the action required by a Service Notice within
        the earlier of 10 business days after a suspension and the period
        specified in the Service Notice, we may (without limiting any other
        right or remedy) terminate these Terms immediately on written notice to
        you.
      </p>
      <p>
        2.3.5 Suspension under clause 2.3.3(b) does not relieve the Subscriber
        of its obligation to pay fees in respect of the period of suspension.
      </p>

      <h3>2.4 Beta Services and Unpaid Pilots</h3>
      <p>
        2.4.1 This clause 2.4 applies if the Subscriber or any of its Authorised
        Users access and use:
      </p>
      <p>
        (a) any feature, functionality or service that we make available on a
        beta, preview, trial, early access, evaluation or pilot basis (
        <strong>Beta Services</strong>); and/or
      </p>
      <p>
        (b) any arrangement under which you are given access to the Services or
        any feature without charge (each, an <strong>Unpaid Pilot</strong>).
      </p>
      <p>2.4.2 The Subscriber acknowledges that:</p>
      <p>
        (a) Beta Services are provided on an "as is" and "as available" basis,
        for evaluation purposes only;
      </p>
      <p>(b) to the maximum extent permitted by law:</p>
      <p>
        (i) we make no representation, warranty, guarantee or condition of any
        kind in relation to Beta Services, whether express or implied, including
        as to availability, reliability, accuracy, completeness, performance,
        fitness for a particular purpose, merchantability, satisfactory quality,
        title or non-infringement;
      </p>
      <p>
        (ii) the warranty in clause 9.1(b) and the availability commitment in
        clause 2.1.2 do not apply to Beta Services; and
      </p>
      <p>
        (iii) access to and use of Beta Services is at the Subscriber's risk.
      </p>
      <p>
        (c) Beta Services have not been subjected to the same testing,
        validation, security review or quality assurance as generally available
        features, and may contain defects and errors, produce materially
        inaccurate or incomplete Outputs, not operate as intended or as
        described, and be subject to interruption, degradation or data loss.
      </p>
      <p>
        2.4.3 We may at any time, in our sole discretion, modify, limit,
        suspend, withdraw or discontinue any Beta Service or Unpaid Pilot, in
        whole or in part, and will give you reasonable prior notice before doing
        so where reasonably practicable. Subject to clause 9.5.3, to the maximum
        extent permitted by law, we will have no liability to you for any such
        modification, limitation, suspension, withdrawal or discontinuation.
      </p>
      <p>
        2.4.4 Nothing in these Terms requires us to make any Beta Service
        generally available, to continue to develop it, or to offer it on the
        same terms (including as to price or functionality) if it is made
        generally available.
      </p>
      <p>
        2.4.5 We may delete Subscriber Content or other data associated with a
        Beta Service or an Unpaid Pilot at or after its conclusion without
        further notice, obligation or liability to the Subscriber.
      </p>

      <h3>2.5 Email Features</h3>
      <p>
        2.5.1 The Services include features that receive, generate and send
        email (together, the <strong>Email Features</strong>), comprising:
      </p>
      <p>
        (a) <strong>Inbound Email</strong>: emails and attachments sent by or on
        behalf of the Subscriber or an Authorised User to an email address that
        we make available for ingestion into the Subscriber's workspace;
      </p>
      <p>
        (b) <strong>Outbound Email</strong>: emails composed, generated or
        assembled through the Services and transmitted by the Subscriber or an
        Authorised User via the Service to recipients the Subscriber or
        Authorised User nominates, including creditors, employees, directors,
        officers, regulators, advisers and other third parties; and
      </p>
      <p>
        (c) <strong>Automated Drafts</strong>: emails automatically drafted by
        the Services and transmitted by the Subscriber or Authorised User in
        response to an Inbound Email or another trigger, in accordance with
        settings, rules or templates that are configured by or on behalf of the
        Subscriber in the Services.
      </p>
      <p>
        2.5.2 <strong>Sub-processors and transmission.</strong>
      </p>
      <p>
        (a) We engage one or more sub-processors to provide the Email Features
        (each an <strong>Email Sub-processor</strong>).
      </p>
      <p>
        (b) Except for storage, transmission and infrastructure providers acting
        in that capacity, our Email Sub-processors do not retain, log or permit
        human review of Subscriber Content or Outputs, except:
      </p>
      <p>(i) as required by applicable law;</p>
      <p>
        (ii) to the extent necessary to transmit, queue, filter or deliver the
        message; or
      </p>
      <p>
        (iii) to the extent necessary to enforce their acceptable use policies.
      </p>
      <p>
        (c) The Subscriber acknowledges that email is an inherently insecure
        medium, that email in transit passes through infrastructure outside our
        control, and that it may not be encrypted end-to-end.
      </p>
      <p>
        2.5.3 <strong>Subscriber as the sender.</strong> In respect of every
        Outbound Email and Automated Draft, the Subscriber acknowledges that the
        Subscriber is:
      </p>
      <p>
        (a) the sender in substance and we act solely as a technical conduit
        transmitting the message on the Subscriber's instruction; and
      </p>
      <p>
        (b) solely responsible for the content, accuracy and legal effect of
        every such communication (including any Output contained in or attached
        to it), the identity and address of every recipient, the configuration
        and testing of all templates, rules and settings, the timing of
        transmission, and determining whether email is an appropriate and
        legally effective method of communication in the circumstances.
      </p>
      <p>
        2.5.4 <strong>Verification before transmission.</strong> The Subscriber
        is solely responsible for:
      </p>
      <p>
        (a) ensuring that every Outbound Email and Automated Draft, and every
        Output contained in or attached to it, is evaluated and approved in
        accordance with clause 6 before transmission; and
      </p>
      <p>
        (b) where Automated Drafts are enabled, reviewing, testing and approving
        in advance the templates, rules and settings that govern them, and for
        monitoring their ongoing operation.
      </p>
      <p>
        2.5.5 <strong>No delivery warranty.</strong> The Subscriber acknowledges
        that:
      </p>
      <p>
        (a) to the extent permitted by law, we do not warrant that any Inbound
        Email will be received, ingested or processed, or that any Outbound
        Email or Automated Drafts will be transmitted, delivered, delivered on
        time, delivered to the intended recipient, or free from blocking,
        quarantine, filtering, delay, rejection, corruption or interception;
      </p>
      <p>
        (b) email delivery depends on systems and providers outside our control;
      </p>
      <p>
        (c) the Services are not a system of record for email correspondence and
        must not be relied upon as one; and
      </p>
      <p>
        (d) the Subscriber is solely responsible for maintaining records of all
        communications sent and received.
      </p>
      <p>
        2.5.6 <strong>Recipients.</strong> The Subscriber must not, and must
        ensure that Authorised Users do not, represent or imply to any person
        that we have prepared, reviewed, approved or accepted responsibility for
        any communication sent through the Email Features.
      </p>

      <h3>2.6 Third Party Services</h3>
      <p>
        2.6.1 Where the Subscriber requires us to integrate the Services with
        any Third Party Services, or we otherwise make Third Party Services
        available to the Subscriber as part of the Services, the Subscriber:
      </p>
      <p>
        (a) acknowledges that the Third Party Services are subject to their own
        terms and conditions between the Subscriber and the provider of a Third
        Party Service;
      </p>
      <p>
        (b) must, at its own cost, obtain and maintain all licences,
        permissions, consents, API access credentials and authorisations
        necessary to enable us to connect to, access, extract data from, and
        exchange data with, each such Third Party Service for the purposes of
        providing the Services;
      </p>
      <p>
        (c) warrants that our access to and use of each Third Party Service in
        accordance with these Terms will not breach any agreement between the
        Subscriber and the provider of that Third Party Service;
      </p>
      <p>
        (d) acknowledges that we are not liable for any failure or limitation in
        the Services to the extent caused by the Subscriber's failure to comply
        with this clause, or by any restriction, suspension, modification or
        discontinuation of access imposed by the provider of a Third Party
        Service.
      </p>
      <p>
        2.7 <strong>Backups.</strong> The Subscriber has the sole responsibility
        for the adequate protection and backup of all data and/or equipment used
        in connection with the Services.
      </p>
      <p>
        2.8 <strong>Personal Information.</strong> To the extent the Subscriber
        gives us access to Personal Information, including in Subscriber Content
        and Inputs, the Subscriber:
      </p>
      <p>
        (a) must obtain the necessary consents and permissions from the relevant
        individuals for us to collect, hold, use and disclose such Personal
        Information as required under these Terms; and
      </p>
      <p>
        (b) warrants that, at the time of disclosure, the collection and
        disclosure of such Personal Information to us, and our collection, use,
        storage and disclosure of such Personal Information for the purpose of
        facilitating the performance of our obligations under these Terms
        complies with applicable privacy and data protection laws; and
      </p>
      <p>
        (c) must promptly notify us in writing if the warranty set out in clause
        2.8(b) ceases to be true or accurate.
      </p>

      <h3>2.9 Suspension</h3>
      <p>
        Notwithstanding anything to the contrary in these Terms, we may suspend
        the Subscriber's, or one or more Authorised User's, access to any
        portion or all of the Services:
      </p>
      <p>(a) immediately without notice if we reasonably determine that:</p>
      <p>
        (i) the Subscriber, or any Authorised User, is using the Services in
        material violation of these Terms or applicable law;
      </p>
      <p>(ii) there is a threat or attack on any of Our IP;</p>
      <p>
        (iii) the use of Our IP by the Subscriber or any Authorised User
        disrupts or poses a security risk to Our IP or to any of our other
        subscribers or vendors;
      </p>
      <p>
        (iv) the Subscriber, or any Authorised User, is using Our IP for
        fraudulent or illegal activities; or
      </p>
      <p>
        (v) our provision of the Services to the Subscriber or any Authorised
        User is prohibited by applicable law; or
      </p>
      <p>(b) in accordance with clause 3.3(b).</p>

      <h2>3. Fees and Payment</h2>
      <p>
        3.1 The Subscriber must pay to us the fees for the Services as agreed
        between the parties at the time of purchase or order, or under the
        applicable order form or pilot agreement.
      </p>
      <p>
        3.2 Unless otherwise agreed in writing, all invoices issued by us are
        due and payable within 7 days of the date of invoice.
      </p>
      <p>
        3.3 If any invoiced amount is not paid by the applicable due date, we
        may (without limiting any other right or remedy):
      </p>
      <p>
        (a) charge interest on the overdue amount at a rate of 10% per annum,
        calculated daily from the due date until the date of payment; and/or
      </p>
      <p>
        (b) suspend the Subscriber and its Authorised Users' access to the
        Services, on not less than 10 business days' prior written notice, until
        the overdue amount is paid in full.
      </p>
      <p>
        3.4 All fees are stated exclusive of GST and any other applicable taxes.
        Where GST applies, it will be added to the relevant invoice and the
        Subscriber must pay the additional amount at the same time as the
        consideration to which it is referable, and on the issue of an invoice
        relating to the supply.
      </p>
      <p>
        3.5 The Subscriber acknowledges that its obligation to pay fees is not
        contingent on
      </p>
      <p>(a) the delivery of any future functionality or feature; or</p>
      <p>
        (b) any statement, representation, roadmap or demonstration not
        expressly set out in these Terms or applicable order form or pilot
        agreement.
      </p>

      <h2>4. Term and Termination</h2>

      <h3>4.1 Term</h3>
      <p>
        4.1.1 These Terms commence on the earlier of the date the Subscriber or
        its Authorised Users first access the Services or the date specified in
        the relevant order form, and continue on a rolling month-to-month basis
        (unless otherwise agreed in the relevant order form) until terminated in
        accordance with this clause 4.
      </p>
      <p>
        4.1.2 Unless otherwise agreed in the relevant order form, each monthly
        term will automatically renew for a further month unless either party
        gives at least 30 days' written notice of non-renewal prior to the end
        of the then-current monthly term.
      </p>

      <h3>4.2 Termination for Cause</h3>
      <p>
        Either party may terminate these Terms immediately on written notice if:
      </p>
      <p>
        (a) the other party commits a material breach that is not remedied
        within 30 days of written notice specifying the breach;
      </p>
      <p>
        (b) the other party becomes insolvent, is placed into administration,
        receivership or liquidation, or makes an assignment for the benefit of
        creditors; or
      </p>
      <p>
        (c) the other party ceases or threatens to cease carrying on business.
      </p>

      <h3>4.3 Termination for Convenience</h3>
      <p>(a) We may terminate these Terms on 90 days' written notice.</p>
      <p>
        (b) The Subscriber may terminate these Terms on 30 days' written notice.
      </p>

      <h3>4.4 Effect of Termination</h3>
      <p>4.4.1 On termination:</p>
      <p>
        (a) all licences and access granted under these Terms immediately cease;
      </p>
      <p>(b) all outstanding fees become immediately due and payable; and</p>
      <p>
        (c) each party must promptly return or destroy the other party's
        Confidential Information, except as required by law.
      </p>
      <p>
        4.4.2 Following termination, we will make the Subscriber Content and
        Outputs available for export for a period of 30 days, unless such data
        is held by, or accessible only through, a Third Party Service, and the
        relevant integration has been disconnected. After that period, we will
        securely delete such data from our systems, subject to any legal
        obligation to retain it.
      </p>
      <p>
        4.4.3 Termination does not affect any rights or obligations that have
        accrued prior to termination or any clauses which by their nature are
        intended to survive termination.
      </p>

      <h2>5. Subscriber Content</h2>
      <p>
        5.1 As between the parties, you retain ownership of all intellectual
        property rights in any documents, data or materials you upload to or
        process through the Services (<strong>Subscriber Content</strong>).
      </p>
      <p>
        5.2 The Subscriber grants us a non-exclusive, royalty-free, worldwide
        licence to use, store, copy, transmit, process, reproduce, distribute
        and display Subscriber Content and perform all acts with respect to
        Subscriber Content as may be necessary for us to provide, secure and
        support the Services, to prevent or address technical or security
        problems, and as required by applicable law.
      </p>
      <p>5.3 The Subscriber represents and warrants that:</p>
      <p>
        (a) it has all necessary rights and permissions to upload and process
        through the Services all material submitted by or on behalf of the
        Subscriber or its Authorised Users (and, in the case of material sent to
        the Services by a third party, the Subscriber is responsible for
        handling that material lawfully once it is received in the Subscriber's
        workspace); and
      </p>
      <p>
        (b) Subscriber Content and its use by us in accordance with these Terms
        do not violate any applicable law or third-party rights.
      </p>
      <p>
        5.4 We do not, and we require that our sub-processors do not, use
        Subscriber Content, Input or Outputs to train, fine-tune or improve any
        artificial intelligence or machine learning model. Except for cloud
        storage and infrastructure providers, our sub-processors do not retain,
        log or permit human review of Subscriber Content, Input or Outputs,
        other than as required by applicable law or to enforce their acceptable
        use policies.
      </p>

      <h2>
        6. Data Ingestion, AI-Generated Outputs and Practitioner Responsibility
      </h2>
      <p>
        6.1 <strong>Nature of the Services.</strong> The Subscriber acknowledges
        and agrees that:
      </p>
      <p>
        (a) the Services ingest Subscriber Content and Inputs, apply automated
        processing and artificial intelligence techniques to extract, classify,
        structure, summarise and reconcile information from that material, and
        generate Outputs;
      </p>
      <p>
        (b) the function of the Services is to organise and surface information
        for review by a suitably qualified practitioner;
      </p>
      <p>
        (c) the Services do not, and are not designed to, exercise professional
        judgement, form opinions, or make determinations, and are not a
        substitute for the professional judgement of a suitably qualified
        practitioner;
      </p>
      <p>
        (d) in no event may any Output, or any part of the Services, be
        construed as, held out as, or relied upon as legal, financial,
        accounting, taxation, valuation, investment, insolvency, regulatory or
        other professional advice, or as a recommendation to take or refrain
        from taking any action; and
      </p>
      <p>
        (e) the Services depend on the completeness, legibility, accuracy,
        currency and integrity of the documents and data supplied to them, and
        the Subscriber is responsible for ensuring that all Subscriber Content
        provided is complete, current and accurate.
      </p>
      <p>
        6.2 <strong>Subscriber responsibility.</strong> The Subscriber is solely
        responsible for:
      </p>
      <p>
        (a) evaluating the suitability of the Services for the Subscriber's
        intended use;
      </p>
      <p>
        (b) the Subscriber's and each Authorised User's use and operation of the
        Services in accordance with relevant laws and standards, including
        ensuring human oversight and monitoring;
      </p>
      <p>
        (c) evaluating (including by human review) the Output for accuracy,
        completeness and other factors relevant to the Subscriber's use before
        using, distributing, or relying on the Output;
      </p>
      <p>
        (d) the Subscriber's and each Authorised User's decisions, actions and
        omissions in reliance on or based on the Output; and
      </p>
      <p>
        (e) maintaining records sufficient to demonstrate that the evaluation
        and review required under this clause 6 and applicable laws and
        standards was performed by or on behalf of the Subscriber.
      </p>
      <p>
        6.3 <strong>Limitations of automated processing.</strong> The Subscriber
        acknowledges that given the Services use artificial intelligence and
        automated data ingestion technologies which are probabilistic and
        rapidly evolving, the Services may:
      </p>
      <p>
        (a) produce Outputs that are incorrect, incomplete, outdated, internally
        inconsistent, misleading, biased or offensive;
      </p>
      <p>
        (b) fail to extract, or incorrectly extract, classify or attribute,
        information contained in a source document, including as a result of
        image quality, handwriting, formatting, corruption or non-standard
        document structure;
      </p>
      <p>
        (c) omit information present in the source material, or include
        information that the source material does not support;
      </p>
      <p>
        (d) produce plausible but factually incorrect statements, figures,
        citations or references;
      </p>
      <p>
        (e) produce different Outputs from the same or similar Input on
        different occasions;
      </p>
      <p>
        (f) produce Outputs that are the same as or similar to outputs the
        Services generate for other subscribers of the Services; and
      </p>
      <p>
        (g) produce Outputs that may not qualify for intellectual property
        protection.
      </p>
      <p>
        6.4 <strong>Professional and statutory obligations.</strong> Nothing in
        the Services or in any Output relieves the Subscriber of, reduces, or
        transfers to us, any duty, obligation or liability imposed on the
        Subscriber by law, by any court or regulator, or by any professional or
        ethical code binding on the Subscriber or any Authorised User.
      </p>
      <p>
        6.5 <strong>No reliance by third parties.</strong> Outputs are provided
        solely for the Subscriber's internal business and professional purposes.
        We owe no duty or liability to any third party who receives or relies on
        any Output. Where the Subscriber provides any Output to a third party,
        the Subscriber must inform that third party that the Output was
        generated using automated and AI-based tools and remains subject to
        verification by the Subscriber.
      </p>

      <h2>7. Intellectual Property</h2>
      <p>7.1 We retain all Intellectual Property Rights in Our IP.</p>
      <p>
        7.2 As between the parties, the Subscriber retains all intellectual
        property rights in Subscriber Content and, to the extent such rights
        subsist, in any Outputs derived from Subscriber Content.
      </p>
      <p>
        7.3 The Subscriber must not, and must ensure that the Authorised Users
        do not, remove, obscure or alter any proprietary notice, watermark,
        attribution or disclaimer contained in the Services or in any Output.
      </p>
      <p>
        7.4 If the Subscriber or any Authorised User provides us with feedback,
        suggestions or ideas about the Services, Beta Services or Unpaid Pilot (
        <strong>Feedback</strong>), the Subscriber grants us a perpetual,
        irrevocable, royalty-free, worldwide licence to use that Feedback in any
        manner for any purpose, without obligation to you.
      </p>
      <p>
        7.5 Notwithstanding anything to the contrary in these Terms, the
        Subscriber acknowledges and agrees that we:
      </p>
      <p>
        (a) may monitor the Subscriber's use of the Services and collect and
        compile Usage Data based on Subscriber Content input into the Services;
      </p>
      <p>
        (b) retain all right, title, interest and Intellectual Property Rights
        in the Usage Data; and
      </p>
      <p>
        (c) may make Usage Data publicly available in compliance with applicable
        law and use Usage Data to the extent and in the manner permitted under
        applicable law, provided that we will not disclose or make available
        Usage Data that contains your Confidential Information except:
      </p>
      <p>(i) in accordance with clause 8; or</p>
      <p>
        (ii) where the Usage Data has been aggregated and de-identified so that
        the Subscriber, its Authorised Users and clients cannot reasonably be
        identified.
      </p>

      <h2>8. Confidentiality</h2>
      <p>
        8.1 Each party agrees to keep confidential all of the other party's
        Confidential Information.
      </p>
      <p>
        8.2 Each party may only use the other party's Confidential Information
        to perform its obligations or exercise its rights under these Terms.
      </p>
      <p>
        8.3 Each party may disclose the other's Confidential Information to its
        employees, contractors and professional advisers on a need-to-know
        basis, provided those persons are bound by equivalent confidentiality
        obligations.
      </p>
      <p>8.4 These obligations do not apply to information that:</p>
      <p>(a) is or becomes publicly known without breach of these Terms;</p>
      <p>
        (b) was already known to the receiving party at the time of disclosure;
      </p>
      <p>
        (c) is independently developed by the receiving party without use of the
        Confidential Information; or
      </p>
      <p>
        (d) is required to be disclosed by law or regulatory order, provided the
        receiving party gives prompt written notice to the disclosing party
        where permitted.
      </p>

      <h2>9. Warranties, Indemnities and Liability</h2>

      <h3>9.1 Our Warranties</h3>
      <p>We warrant that:</p>
      <p>(a) we have the right to grant the licences in these Terms;</p>
      <p>
        (b) we will use commercially reasonable efforts to provide the Services
        in a professional and workmanlike manner; and
      </p>
      <p>
        (c) we will implement and maintain reasonable technical and
        organisational security measures to protect Subscriber Content.
      </p>

      <h3>9.2 Disclaimer</h3>
      <p>
        Except as set out in clause 9.1, and to the maximum extent permitted by
        law:
      </p>
      <p>
        (a) we expressly disclaim and exclude all conditions, warranties or
        other terms which might have effect between the parties or be implied or
        incorporated into this Agreement or any collateral contract, whether by
        legislation, common law or otherwise;
      </p>
      <p>
        (b) the Subscriber acknowledges that the Services are provided on an "as
        is" basis;
      </p>
      <p>(c) we do not warrant that:</p>
      <p>
        (i) the Services will be uninterrupted, error-free or free of harmful
        components;
      </p>
      <p>
        (ii) any Output will be accurate, complete or suitable for any
        particular purpose;
      </p>
      <p>
        (iii) the Services will meet the Subscriber's specific requirements;
      </p>
      <p>
        (iv) any Output will be free from error, omission or fabrication, or
        will correctly and completely reflect the content of the source material
        ingested; or
      </p>
      <p>
        (v) the Services will detect, identify or extract every item of
        information contained in any document or data set provided to us.
      </p>

      <h3>9.3 Australian Consumer Law</h3>
      <p>
        9.3.1 Nothing in these Terms excludes or limits any guarantee, warranty
        or right conferred by the Australian Consumer Law that cannot lawfully
        be excluded or limited.
      </p>
      <p>
        9.3.2 Our liability for breach of a guarantee conferred by the
        Australian Consumer Law (other than those conferred by sections 51 to 53
        of the Australian Consumer Law) is limited:
      </p>
      <p>
        (a) in the case of goods, to any one of the following as determined by
        us:
      </p>
      <p>(i) the replacement of the goods or the supply of equivalent goods;</p>
      <p>(ii) the repair of the goods;</p>
      <p>
        (iii) the payment of the cost of replacing the goods or of acquiring
        equivalent goods; or
      </p>
      <p>(iv) the payment of the cost of having the goods repaired; and</p>
      <p>
        (b) in the case of services (including the Services), to any one of the
        following as determined by us:
      </p>
      <p>(i) the supplying of the services again; or</p>
      <p>(ii) the payment of the cost of having the services supplied again.</p>

      <h3>9.4 Indemnities</h3>
      <p>
        We will defend the Subscriber, or at our option, settle any claim or
        action brought against the Subscriber, whether at common law, under tort
        (including negligence), in equity, pursuant to statute or otherwise, by
        a third party alleging that the Subscriber's use of the Services in
        accordance with these Terms infringe that third party's Intellectual
        Property Rights, and will indemnify you against damages and costs
        finally awarded against you, or agreed by us in settlement, in respect
        of such a claim or action, except to the extent the claim or action
        arises from:
      </p>
      <p>(a) Subscriber Content or Input;</p>
      <p>(b) use of the Services or an Output in breach of these Terms;</p>
      <p>
        (c) use of an Output where the Subscriber knew or ought reasonably to
        have known that such use was likely to be infringing;
      </p>
      <p>
        (d) any material, data or technology not supplied by us, including any
        Third Party Services;
      </p>
      <p>(e) any modification of the Services not made by us.</p>
      <p>
        9.4.1 If your use of the Services infringes, or in our reasonable
        opinion is likely to infringe, on any third party Intellectual Property
        Rights, we may, at our option:
      </p>
      <p>
        (a) procure the right for the Subscriber to continue using the Services;
      </p>
      <p>
        (b) modify or replace the affected Services with functionally equivalent
        services; or
      </p>
      <p>
        (c) if neither (a) nor (b) is commercially reasonable, terminate these
        Terms and refund any prepaid unused fees.
      </p>
      <p>
        9.4.2 The Subscriber will defend us, our Affiliates and our respective
        officers, employees and contractors against any claim or action brought
        by a third party arising out of or relating to:
      </p>
      <p>(a) Subscriber Content or Input;</p>
      <p>
        (b) any Output used, disclosed, transmitted, filed, lodged or relied
        upon by the Subscriber or its personnel, including any Outbound Email or
        Automated Drafts;
      </p>
      <p>(c) breach of clause 2.3, clause 2.5 or clause 5.3;</p>
      <p>
        (d) any advice, report, determination, opinion or communication given by
        the Subscriber that was based on or informed by an Output; or
      </p>
      <p>(e) breach of any professional, statutory or regulatory obligation,</p>
      <p>
        and will indemnify us against damages and costs finally awarded against
        us, or agreed by you in settlement, in respect of such a claim.
      </p>
      <p>
        9.4.3 Each indemnity in this clause 9.4 is conditional on the
        indemnified party:
      </p>
      <p>
        (a) promptly notifying the indemnifying party in writing of the claim;
      </p>
      <p>
        (b) giving the indemnifying party control of the defence and settlement
        of the claim, provided that no settlement that imposes a non-monetary
        obligation on, or requires an admission of fault by, the indemnified
        party may be made without its prior written consent (not to be
        unreasonably withheld); and
      </p>
      <p>
        (c) providing reasonable cooperation at the indemnifying party's
        expense. A failure to notify promptly does not relieve the indemnifying
        party of its obligations except to the extent it is materially
        prejudiced by the delay.
      </p>

      <h3>9.5 Limitation of Liability</h3>
      <p>
        9.5.1 <strong>Exclusion and limitation of liability.</strong> Subject to
        clause 9.5.3, except to the extent that a liability cannot be excluded,
        restricted or modified under the applicable law (including as to any
        terms, conditions and warranties which the law expressly provides may
        not be excluded, restricted or modified, or may be excluded, restricted
        or modified only to a limited extent):
      </p>
      <p>
        (a) we are not liable for any loss, damage, cost or expense (whether
        arising under contract (including any indemnity), tort, equity, statute
        or otherwise) in connection with:
      </p>
      <p>
        (i) the Subscriber or its Authorised User's use of, or reliance on, any
        Output;
      </p>
      <p>
        (ii) any decision, advice, report, determination, opinion, communication
        or filing that was based on or informed by an Output;
      </p>
      <p>
        (iii) any claim by the Subscriber's client, a creditor, a director, an
        officer, an employee, a regulator or any other third party arising out
        of an Output; or
      </p>
      <p>
        (iv) the accuracy, completeness or availability of any third-party data
        source or public register accessed through the Services; and
      </p>
      <p>(b) each party's total aggregate liability to the other party for:</p>
      <p>
        (i) all loss or claims arising under or in connection with Unpaid Pilots
        is limited to AUD $500;
      </p>
      <p>
        (ii) all loss or claims arising under or in connection with Beta
        Services is limited to the lesser of the fees actually paid by the
        Subscriber for that Beta Service in the 3 months preceding the event
        giving rise to the claim, and AUD $5,000; and
      </p>
      <p>
        (iii) all other loss or claims arising under or in connection with these
        Terms is limited to the lesser of the fees paid by the Subscriber in the
        12 months preceding the event giving rise to the claim, and AUD $10,000.
      </p>
      <p>
        9.5.2 <strong>Consequential loss.</strong> Neither party will be liable
        to the other for any indirect, consequential, special, punitive or
        exemplary loss or damage or for any loss of profit, loss of revenue,
        loss of data, loss of goodwill or reputational harm, regardless of the
        cause of action and whether or not that party has been advised of the
        possibility of such loss.
      </p>
      <p>
        9.5.3 <strong>Exclusions.</strong> Nothing in these Terms limits or
        excludes liability for:
      </p>
      <p>(a) death or personal injury caused by negligence;</p>
      <p>(b) fraud or wilful misconduct; or</p>
      <p>(c) any liability that cannot lawfully be limited or excluded.</p>
      <p>
        9.5.4 <strong>Proportional liability and mitigation.</strong> Neither
        party is liable for loss to the extent that loss:
      </p>
      <p>
        (a) was caused or contributed to by the other party's breach of these
        Terms or applicable laws, or negligence, wrongful act or wrongful
        omission; or
      </p>
      <p>
        (b) could have been avoided or reduced by the other party taking
        reasonable steps to mitigate that loss.
      </p>
      <p>
        9.5.5 <strong>Independent operation.</strong> Each limitation and
        exclusion in these Terms is a separate provision, severable from every
        other, and operates to the maximum extent permitted by law even if any
        other limitation or exclusion is held to be unenforceable.
      </p>

      <h2>10. General</h2>

      <h3>10.1 Governing Law</h3>
      <p>
        These Terms are governed by the laws of Tasmania, Australia. Each party
        submits to the non-exclusive jurisdiction of the courts of Tasmania and
        the Federal Court of Australia.
      </p>

      <h3>10.2 Entire Agreement</h3>
      <p>
        These Terms, any order form or pilot agreement which references these
        Terms, and our Acceptable Use Policy (as published at{" "}
        <a
          href="https://trust.morgan.so/"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://trust.morgan.so/
        </a>{" "}
        or otherwise notified to the Subscriber) constitute the entire agreement
        between the parties in relation to the Services and supersede all prior
        representations, discussions and agreements.
      </p>

      <h3>10.3 Assignment</h3>
      <p>
        The Subscriber must not assign or transfer any of its rights or
        obligations under these Terms without our prior written consent. We may
        assign our rights and obligations to an Affiliate or in connection with
        a merger, acquisition or sale of substantially all of our assets, on
        written notice to the Subscriber.
      </p>

      <h3>10.4 Force Majeure</h3>
      <p>
        Neither party is liable for any delay or failure to perform its
        obligations (other than a payment obligation) to the extent caused by
        circumstances beyond its reasonable control, including natural
        disasters, acts of government, cyberattacks on third-party
        infrastructure, or widespread internet outages.
      </p>

      <h3>10.5 Waiver and Severability</h3>
      <p>
        A failure by either party to exercise any right under these Terms does
        not constitute a waiver. If any provision of these Terms is found to be
        unenforceable, the remaining provisions continue in full force.
      </p>

      <h3>10.6 Notices</h3>
      <p>Notices under these Terms must be in writing and sent to:</p>
      <p>
        Morgan: <a href="mailto:legal@morgan.so">legal@morgan.so</a>
      </p>
      <p>
        Subscriber: the contact details provided to us when the Subscriber
        creates an account, or otherwise via the Services
      </p>

      <h3>10.7 Relationship of Parties</h3>
      <p>
        The parties are independent contractors. Nothing in these Terms creates
        a partnership, joint venture, agency or employment relationship.
      </p>

      <h3>10.8 Export Control and Sanctions</h3>
      <p>The Subscriber:</p>
      <p>
        (a) represents and warrants that it and its Affiliates are not located
        in, organised under the laws of, or ordinarily resident in any
        Sanctioned Jurisdiction, and are not designated on any sanctions or
        restricted party list maintained by the Australian Government, the
        United Nations Security Council, the United States, the European Union
        or the United Kingdom; and
      </p>
      <p>
        (b) must comply with all applicable export control and sanctions laws in
        the Subscriber's use of the Services and of any Output.
      </p>

      <h3>10.9 Marketing</h3>
      <p>
        The Subscriber grants us a non-exclusive right to identify the
        Subscriber as a customer of the Services and to use the Subscriber's
        name and logo for that purpose in our marketing and public relations
        materials, unless we receive written notice from the Subscriber
        withdrawing such right, in which case we will cease further use within a
        reasonable period of receiving such notice.
      </p>

      <h3>10.10 Equitable Relief</h3>
      <p>
        Each party acknowledges that a breach of clause 7 or clause 8 may cause
        harm for which damages are not an adequate remedy, and that the other
        party is entitled to seek injunctive or other equitable relief in
        addition to any other remedy available to it.
      </p>

      <h2>11. Definitions and interpretation</h2>

      <h3>11.1 Definitions</h3>
      <p>In these Terms:</p>
      <p>
        <strong>Affiliate</strong> means an entity that directly or indirectly
        controls, is controlled by, or is under common control with, a party.
      </p>
      <p>
        <strong>Australian Consumer Law</strong> means Schedule 2 to the
        Competition and Consumer Act 2010 (Cth).
      </p>
      <p>
        <strong>Authorised User</strong> means any employee or contractor of the
        Subscriber given a user account for the Services, and includes the
        Subscriber's administrators.
      </p>
      <p>
        <strong>Automated Drafts</strong> has the meaning given in clause
        2.5.1(c).
      </p>
      <p>
        <strong>Beta Services</strong> has the meaning given in clause 2.4.1.
      </p>
      <p>
        <strong>Confidential Information</strong> means information that is by
        its nature proprietary, confidential or commercial-in-confidence, and in
        respect of us, includes the existence, features, functionality and
        performance of any Beta Service, but does not include any information
        which:
      </p>
      <p>
        (a) was already known to the receiving party at the time of disclosure
        by the other party; or
      </p>
      <p>
        (b) is independently developed by the receiving party without use of the
        Confidential Information; or
      </p>
      <p>(c) is or becomes publicly known without breach of these Terms.</p>
      <p>
        <strong>Email Features</strong> has the meaning given in clause 2.5.1.
      </p>
      <p>
        <strong>Inbound Email</strong> has the meaning given in clause 2.5.1(a).
      </p>
      <p>
        <strong>Input</strong> means information, data, materials, text, images,
        code, works or other content that is input, entered, posted, uploaded,
        submitted, transferred or otherwise transmitted to the Services by or on
        behalf of the Subscriber or any Authorised User, and includes all
        Inbound Emails.
      </p>
      <p>
        <strong>Intellectual Property Rights</strong> means all present and
        future rights conferred by statute, common law or equity in or in
        relation to any copyright, trade marks, designs, patents, circuit
        layouts, business and domain names, inventions, and other results of
        intellectual activity in the industrial, commercial, scientific,
        literary or artistic fields.
      </p>
      <p>
        <strong>Our IP</strong> means all Intellectual Property Rights in the
        Services, including the platform, models, workflows, software, and all
        underlying technology, and any templates, formats, dashboards and the
        like as modified or improved from time to time that are accessible by or
        generated from the Subscriber's use of the Services, including Usage
        Data and any other information, data, or other content derived from our
        monitoring of the Subscriber's access to or use of the Services, but
        excluding any Subscriber Content.
      </p>
      <p>
        <strong>Outbound Email</strong> has the meaning given in clause
        2.5.1(b).
      </p>
      <p>
        <strong>Output</strong> means information, data, materials, text,
        images, code, works or other content generated by or otherwise output
        from the Services in response to an Input, and includes all Outbound
        Emails and Automated Drafts.
      </p>
      <p>
        <strong>Personal Information</strong> has the meaning given in the
        Privacy Act 1988 (Cth).
      </p>
      <p>
        <strong>Sanctioned Jurisdiction</strong> means a country or territory
        subject to comprehensive sanctions administered or enforced by the
        Australian Government, the United Nations Security Council, the United
        States, the European Union or the United Kingdom.
      </p>
      <p>
        <strong>Subscriber</strong> has the meaning given in clause 1.1.
      </p>
      <p>
        <strong>Subscriber Content</strong> has the meaning given in clause 5.1.
      </p>
      <p>
        <strong>suitably qualified practitioner</strong> means a natural person
        who holds the professional qualifications, registrations, licences and
        current experience necessary to perform, review and take professional
        responsibility for the relevant work under applicable law and under any
        professional code binding on the Subscriber.
      </p>
      <p>
        <strong>Term</strong> means the period commencing under clause 4.1.1 and
        continuing until these Terms are terminated in accordance with clause 4,
        including any renewal period under clause 4.1.2.
      </p>
      <p>
        <strong>Third Party Services</strong> means any products, technology,
        content, data, services, information, websites or other materials that
        are owned by third parties and are included in, incorporated into or
        accessible through the Services, including any email service, document
        management system, practice management system and internet browser.
      </p>
      <p>
        <strong>Unpaid Pilot</strong> has the meaning given in clause 2.4.1.
      </p>
      <p>
        <strong>Usage Data</strong> means information reflecting access to and
        use of the Services, including frequency, duration, volume, feature and
        functionality usage, and statistical or analytical data derived from
        that information, but excludes Subscriber Content, Input and Outputs.
      </p>

      <h3>11.2 Interpretation</h3>
      <p>In these Terms, unless otherwise stated:</p>
      <p>(a) the singular includes the plural and vice versa;</p>
      <p>(b) a gender includes the other genders;</p>
      <p>
        (c) other grammatical forms of defined words or expressions have
        corresponding meanings;
      </p>
      <p>
        (d) headings and sub-headings are used for convenience only and do not
        affect the interpretation of these Terms;
      </p>
      <p>
        (e) no provision of these Terms may be construed adversely to a party
        solely on the ground that the party was responsible for the preparation
        of these Terms or the preparation or proposal of that provision;
      </p>
      <p>
        (f) wherever "include", "for example", or any form of those words or
        similar expression is used, it means including without limitation;
      </p>
      <p>
        (g) a reference to a party is to a party to these Terms and a reference
        to a party to a document includes the party's executors, administrators,
        successors and permitted assigns and substitutes (including by
        novation);
      </p>
      <p>
        (h) if something is to be or may be done on a day that is not a business
        day then it must be done on the next business day;
      </p>
      <p>
        (i) the word "person" includes a natural person, partnership, body
        corporate, association, governmental or other authority and any body or
        entity whether incorporated or not;
      </p>
      <p>
        (j) if a period of time is specified and dates from a given day or the
        day of an act or event, it will be calculated exclusive of that day; and
      </p>
      <p>
        (k) the word "month" means calendar month and the word "year" means
        twelve (12) months.
      </p>
    </LegalLayout>
  );
}
